/* CesarFilho 932048016 */
/* frontend/src/components/SidebarNav.tsx — VERSÃO PREVIEW DEMO 2026-05-21 */

/**
 * Movatic WMS — Menu lateral (polish demo)
 *
 * Mudanças vs versão anterior:
 *  - T-codes monospace ao lado de cada item (style SAP)
 *  - Logo + tenant + "PREVIEW" badge no header
 *  - Espaçamento, tipografia e contraste refinados
 *  - Hover states mais suaves
 *  - Grupos com letra-spacing e separação visual
 *
 * Mantém TODA a lógica original: favoritos, activeMatch, roles, compact mode.
 */

import React, { useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  PanelLeft,
  PanelLeftClose,
  Star,
  StarOff,
  LogOut,
} from 'lucide-react'

import { MENUS, MenuItem } from '@/nav/menu'
import { useAuthStore } from '@/store/authStore'
import { useNavStore } from '@/store/navStore'
import { shouldShow, flattenLeafs } from '@/nav/menu-utils'

function trimSlashEnd(path?: string) {
  if (!path) return path
  return path !== '/' ? path.replace(/\/+$/, '') : path
}

function pathMatches(pathname: string, itemPath?: string): boolean {
  if (!itemPath) return false
  const current = trimSlashEnd(pathname)
  const expected = trimSlashEnd(itemPath)
  if (!current || !expected) return false
  return current === expected || current.startsWith(`${expected}/`)
}

function activeByAlias(item: MenuItem, pathname: string): boolean {
  const activeMatch = item.activeMatch
  if (!activeMatch) return false
  if (Array.isArray(activeMatch)) {
    return activeMatch.some((regex) => regex.test(pathname))
  }
  return activeMatch.test(pathname)
}

function itemIsActive(item: MenuItem, pathname: string): boolean {
  return pathMatches(pathname, item.path) || activeByAlias(item, pathname)
}

function treeHasActive(item: MenuItem, pathname: string): boolean {
  if (itemIsActive(item, pathname)) return true
  if (!item.children?.length) return false
  return item.children.some((child) => treeHasActive(child, pathname))
}

/* ============================================================
   T-CODE BADGE — monospace, subtle
   ============================================================ */
function TCodeBadge({ code }: { code: string }) {
  // Sigla de submenu (WK00, CO01...): ambar #FFAF24, letra escura, 10px, peso 500.
  return (
    <span
      className="shrink-0 font-mono text-[10px] tracking-tight px-1.5 py-0.5 rounded transition-colors duration-150"
      style={{ backgroundColor: '#FFAF24', color: '#1a1200', fontWeight: 500 }}
    >
      {code}
    </span>
  )
}

function SidebarItem({ item, depth = 0 }: { item: MenuItem; depth?: number }) {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const favorites = useNavStore((state) => state.favorites)
  const toggleFavorite = useNavStore((state) => state.toggleFavorite)

  if (!shouldShow(item, user)) return null

  const isFavorite = favorites.includes(item.id)
  const active = itemIsActive(item, location.pathname)
  const Icon = item.icon

  const labelContent = (
    <span
      className="grid items-center gap-2.5 flex-1 min-w-0"
      style={{
        gridTemplateColumns: '22px 1fr auto',
        paddingLeft: `${depth * 14}px`,
      }}
    >
      {Icon ? (
        <Icon size={16} className="shrink-0" style={{ color: '#FFAF24' }} />
      ) : (
        <span />
      )}
      <span className="truncate">{item.label}</span>
      {item.tcode && <TCodeBadge code={item.tcode} />}
    </span>
  )

  if (item.path) {
    return (
      <div className="group relative px-2">
        <NavLink
          to={item.path}
          className={({ isActive }) =>
            [
              'navlink flex items-center w-full pr-10 py-2 px-3 rounded-md text-sm',
              'transition-all duration-150',
              isActive || active ? 'is-active' : '',
            ].join(' ')
          }
        >
          {labelContent}
        </NavLink>

        <button
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            toggleFavorite(item.id)
          }}
          title={
            isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'
          }
          className={[
            'absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md',
            'text-slate-300 opacity-0 transition group-hover:opacity-100',
            'hover:bg-slate-700 hover:text-yellow-300 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500',
          ].join(' ')}
        >
          {isFavorite ? <Star size={14} /> : <StarOff size={14} />}
        </button>
      </div>
    )
  }

  if (item.href) {
    return (
      <div className="px-2">
        <a
          href={item.href}
          target={item.target || '_blank'}
          rel="noreferrer"
          className="navlink flex items-center w-full pr-9 py-2 px-3 rounded-md text-sm transition-all duration-150"
        >
          {labelContent}
        </a>
      </div>
    )
  }

  return (
    <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-300">
      {item.label}
    </div>
  )
}

function SidebarBranch({
  item,
  depth = 0,
}: {
  item: MenuItem
  depth?: number
}) {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const collapsedSections = useNavStore((state) => state.collapsedSections)
  const toggleSection = useNavStore((state) => state.toggleSection)

  if (!shouldShow(item, user)) return null

  const visibleChildren = (item.children || []).filter((child) =>
    shouldShow(child, user),
  )

  if (visibleChildren.length === 0) {
    return <SidebarItem item={item} depth={depth} />
  }

  const Icon = item.icon
  const hasActive = treeHasActive(item, location.pathname)
  const manualState = collapsedSections[item.id]
  const collapsed = hasActive ? false : manualState === true

  return (
    <div className="mt-2 first:mt-0">
      <button
        type="button"
        onClick={() => toggleSection(item.id)}
        className={[
          'flex w-full items-center justify-between rounded-md px-3 py-2 text-left',
          'text-[11px] font-bold uppercase tracking-[0.08em]',
          'transition-all duration-150',
        ].join(' ')}
        style={{ backgroundColor: '#FFAF24', color: '#1a1200' }}
        aria-expanded={!collapsed}
      >
        <span
          className="flex min-w-0 items-center gap-2 flex-1"
          style={{ paddingLeft: `${depth * 14}px` }}
        >
          {/* icone + chevron herdam o color #1a1200 do botao (currentColor) */}
          {Icon ? <Icon size={14} className="shrink-0" /> : null}
          <span className="truncate">{item.label}</span>
          {item.tcode && (
            <span
              className="font-mono text-[14px] tracking-tight px-1 py-0.5 rounded ml-auto mr-1"
              style={{
                backgroundColor: '#e09a10',
                color: '#1a1200',
                fontWeight: 500,
              }}
            >
              {item.tcode}
            </span>
          )}
        </span>
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>

      {!collapsed && (
        <div className="mt-1 flex flex-col gap-0.5">
          {visibleChildren.map((child) => {
            if (child.children?.length) {
              return (
                <SidebarBranch key={child.id} item={child} depth={depth + 1} />
              )
            }
            return <SidebarItem key={child.id} item={child} depth={depth + 1} />
          })}
        </div>
      )}
    </div>
  )
}

function CompactSidebar() {
  const user = useAuthStore((state) => state.user)
  const favorites = useNavStore((state) => state.favorites)
  const setSidebarCollapsed = useNavStore((state) => state.setSidebarCollapsed)

  const allLeafs = flattenLeafs(MENUS, user)
  const favoriteItems = allLeafs.filter((item) => favorites.includes(item.id))

  const itemsToShow =
    favoriteItems.length > 0 ? favoriteItems : allLeafs.slice(0, 18)

  return (
    <nav
      aria-label="Navegação lateral compacta"
      className="flex h-full w-16 flex-col bg-[#0b2340] p-2 text-white"
    >
      <div className="flex items-center justify-center pb-2">
        <button
          type="button"
          className="iconbtn"
          onClick={() => setSidebarCollapsed(false)}
          title="Expandir menu"
        >
          <PanelLeft size={18} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {itemsToShow.map((item) => {
          const Icon = item.icon

          if (item.path) {
            return (
              <NavLink
                key={item.id}
                to={item.path}
                title={`${item.label}${item.tcode ? ` • ${item.tcode}` : ''}`}
                className={({ isActive }) =>
                  ['iconbtn', isActive ? 'ring-2 ring-blue-400' : ''].join(' ')
                }
              >
                {Icon ? <Icon size={18} /> : <span>•</span>}
              </NavLink>
            )
          }

          if (item.href) {
            return (
              <a
                key={item.id}
                href={item.href}
                target={item.target || '_blank'}
                rel="noreferrer"
                title={item.label}
                className="iconbtn"
              >
                {Icon ? <Icon size={18} /> : <span>•</span>}
              </a>
            )
          }

          return null
        })}
      </div>
    </nav>
  )
}

export default function SidebarNav() {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const nav = useNavigate()
  const favorites = useNavStore((state) => state.favorites)
  const sidebarCollapsed = useNavStore((state) => state.sidebarCollapsed)
  const setSidebarCollapsed = useNavStore((state) => state.setSidebarCollapsed)
  const resetNavigation = useNavStore((state) => state.resetNavigation)

  const allLeafs = flattenLeafs(MENUS, user)
  const favoriteItems = allLeafs.filter((item) => favorites.includes(item.id))

  useEffect(() => {
    document.documentElement.dataset.sidebar = sidebarCollapsed
      ? 'compact'
      : 'expanded'
  }, [sidebarCollapsed])

  if (sidebarCollapsed) {
    return <CompactSidebar />
  }

  return (
    <nav
      aria-label="Navegação lateral"
      className="flex h-full flex-col bg-[#0b2340] text-white"
    >
      {/* Header com branding */}
      <div className="border-b border-[#1f3454] px-3 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-bold tracking-tight">
                Movatic WMS
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-0.5">
              <span className="truncate">
                {user?.company?.name || user?.username || '—'}
              </span>
              <button
                type="button"
                onClick={() => {
                  logout()
                  nav('/login', { replace: true })
                }}
                title="Sair"
                aria-label="Sair"
                className="shrink-0 cursor-pointer text-neutral-400 transition-colors hover:text-white"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>

          <button
            type="button"
            className="iconbtn"
            onClick={() => setSidebarCollapsed(true)}
            title="Recolher menu"
          >
            <PanelLeftClose size={18} />
          </button>
        </div>
      </div>

      {/* Favoritos */}
      {favoriteItems.length > 0 && (
        <div className="border-b border-[#1f3454] px-3 py-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
            ★ Favoritos
          </div>
          <div className="flex flex-col gap-0.5">
            {favoriteItems.map((item) => (
              <SidebarItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Menus principais */}
      <div className="flex-1 overflow-y-auto px-2 py-3">
        {MENUS.map((item) => (
          <SidebarBranch key={item.id} item={item} />
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-[#1f3454] p-3">
        <div className="text-[9px] text-slate-500 text-center mb-2">
          339 primitivas · 20 módulos
        </div>
        <button
          type="button"
          onClick={resetNavigation}
          className="w-full rounded-md border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 transition-all hover:bg-slate-800 hover:border-slate-600 hover:text-white"
          title="Reabre grupos e limpa favoritos locais"
        >
          Resetar menu
        </button>
      </div>
    </nav>
  )
}
